import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { ZoomIn, ZoomOut, Maximize2, RotateCcw, AlertTriangle } from 'lucide-react';
import { useFlowEditor } from '../../hooks/useFlowEditor';
import { validateFlowIntegrity } from '../../hooks/useConditionalLogic';
import { resolvePages } from '../../lib/formPages';
import { FlowNode } from './FlowNode';
import { FlowConnection, GhostConnection } from './FlowConnection';
import { FlowConditionEditor } from './FlowConditionEditor';
import { RuleEditorModal } from './editor/RuleEditorModal';
import type { Form, FormField, FormPage, FieldRule, ScoreVariable } from '../../types/forms';

interface FlowEditorProps {
  fields: FormField[];
  onFieldsChange: (fields: FormField[]) => void;
  scoringVariables?: ScoreVariable[];
  /** Form pages — enables page-level jump targets in the rule editor (Story 5.7). */
  pages?: FormPage[];
  className?: string;
}

export const FlowEditor: React.FC<FlowEditorProps> = ({
  fields,
  onFieldsChange,
  scoringVariables = [],
  pages,
  className = '',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showValidation, setShowValidation] = useState(true);
  const [rulesModalFieldId, setRulesModalFieldId] = useState<string | null>(null);
  const [draggingFrom, setDraggingFrom] = useState<{
    fieldId: string;
    startX: number;
    startY: number;
  } | null>(null);
  const [ghostEnd, setGhostEnd] = useState<{ x: number; y: number } | null>(null);
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);
  const touchStartRef = useRef<{ distance: number; midX: number; midY: number; zoom: number; pan: { x: number; y: number } } | null>(null);

  const flow = useFlowEditor({ fields, onFieldsChange, pages });

  const rulesModalField = rulesModalFieldId ? fields.find(f => f.id === rulesModalFieldId) : null;

  const handleUpdateRules = useCallback((fieldId: string, rules: FieldRule[]) => {
    const updated = fields.map(f => f.id === fieldId ? { ...f, rules } : f);
    onFieldsChange(updated);
  }, [fields, onFieldsChange]);

  const handleOpenRules = useCallback((fieldId: string) => {
    setRulesModalFieldId(fieldId);
  }, []);

  // Refs for stable access in native event listeners
  const zoomRef = useRef(flow.zoom);
  const panRef = useRef(flow.pan);
  zoomRef.current = flow.zoom;
  panRef.current = flow.pan;

  // Native wheel handler - detects trackpad pinch vs scroll-pan vs mouse scroll
  const handleWheelNative = useCallback((e: WheelEvent) => {
    e.preventDefault();

    if (e.ctrlKey) {
      // Trackpad pinch-to-zoom (browsers send ctrlKey + deltaY for pinch)
      const zoomDelta = -e.deltaY * 0.01;
      const newZoom = Math.min(Math.max(zoomRef.current + zoomDelta, 0.3), 3);
      flow.setZoom(newZoom);
    } else {
      // Trackpad 2-finger pan OR mouse scroll
      // deltaMode 0 = pixels (trackpad), 1 = lines (mouse)
      const isTrackpad = e.deltaMode === 0 && Math.abs(e.deltaX) + Math.abs(e.deltaY) < 100;

      if (isTrackpad || Math.abs(e.deltaX) > 0) {
        // Pan: trackpad 2-finger gesture
        flow.setPan({
          x: panRef.current.x - e.deltaX,
          y: panRef.current.y - e.deltaY,
        });
      } else {
        // Mouse scroll: zoom (original behavior)
        const delta = e.deltaY > 0 ? -0.05 : 0.05;
        flow.setZoom(Math.min(Math.max(zoomRef.current + delta, 0.3), 3));
      }
    }
  }, [flow.setZoom, flow.setPan]);

  // Non-passive wheel listener for preventDefault support
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener('wheel', handleWheelNative, { passive: false });
    return () => container.removeEventListener('wheel', handleWheelNative);
  }, [handleWheelNative]);

  // Safari gesture events (pinch-to-zoom)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let lastScale = 1;

    const handleGestureStart = (e: Event) => {
      e.preventDefault();
      lastScale = 1;
    };

    const handleGestureChange = (e: Event) => {
      e.preventDefault();
      const ge = e as any;
      const scaleDelta = ge.scale - lastScale;
      lastScale = ge.scale;
      const newZoom = Math.min(Math.max(zoomRef.current + scaleDelta * 0.5, 0.3), 3);
      flow.setZoom(newZoom);
    };

    container.addEventListener('gesturestart', handleGestureStart, { passive: false });
    container.addEventListener('gesturechange', handleGestureChange, { passive: false });

    return () => {
      container.removeEventListener('gesturestart', handleGestureStart);
      container.removeEventListener('gesturechange', handleGestureChange);
    };
  }, [flow.setZoom]);

  // Touch events for tablet/mobile (pinch-to-zoom + 2-finger pan)
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const t1 = e.touches[0];
      const t2 = e.touches[1];
      const distance = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
      const midX = (t1.clientX + t2.clientX) / 2;
      const midY = (t1.clientY + t2.clientY) / 2;
      touchStartRef.current = {
        distance,
        midX,
        midY,
        zoom: flow.zoom,
        pan: { ...flow.pan },
      };
    }
  }, [flow.zoom, flow.pan]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2 && touchStartRef.current) {
      e.preventDefault();
      const t1 = e.touches[0];
      const t2 = e.touches[1];
      const distance = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
      const midX = (t1.clientX + t2.clientX) / 2;
      const midY = (t1.clientY + t2.clientY) / 2;

      // Zoom based on distance ratio
      const ratio = distance / touchStartRef.current.distance;
      const newZoom = Math.min(Math.max(touchStartRef.current.zoom * ratio, 0.3), 3);
      flow.setZoom(newZoom);

      // Pan based on midpoint delta
      const dx = midX - touchStartRef.current.midX;
      const dy = midY - touchStartRef.current.midY;
      flow.setPan({
        x: touchStartRef.current.pan.x + dx,
        y: touchStartRef.current.pan.y + dy,
      });
    }
  }, [flow.setZoom, flow.setPan]);

  const handleTouchEnd = useCallback(() => {
    touchStartRef.current = null;
  }, []);

  // Pan handlers
  const handleCanvasMouseDown = useCallback((e: React.MouseEvent) => {
    // Only pan on left click on empty area
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest('[data-port]') || target.closest('[data-node]')) return;

    // Deselect
    flow.selectNode(null);
    flow.selectConnection(null);

    setIsPanning(true);
    panStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      panX: flow.pan.x,
      panY: flow.pan.y,
    };
  }, [flow]);

  // Delete/Backspace corta a ligação selecionada (mesmo gesto do n8n). O botão
  // "Remover" do popover continua valendo — este é o caminho rápido.
  useEffect(() => {
    const selectedId = flow.selectedConnectionId;
    if (!selectedId) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;

      // Não roubar a tecla de quem está digitando (campos do popover de condição)
      const target = e.target as HTMLElement | null;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        target?.isContentEditable
      ) {
        return;
      }

      e.preventDefault();
      flow.removeConnection(selectedId);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [flow.selectedConnectionId, flow.removeConnection]);

  useEffect(() => {
    if (!isPanning) return;

    const handleMove = (e: MouseEvent) => {
      if (!panStartRef.current) return;
      const dx = e.clientX - panStartRef.current.x;
      const dy = e.clientY - panStartRef.current.y;
      flow.setPan({
        x: panStartRef.current.panX + dx,
        y: panStartRef.current.panY + dy,
      });
    };

    const handleUp = () => {
      setIsPanning(false);
      panStartRef.current = null;
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [isPanning, flow]);

  // Ghost connection drag
  const handleStartConnection = useCallback((fieldId: string, e: React.MouseEvent) => {
    const node = flow.nodes.find(n => n.fieldId === fieldId);
    if (!node) return;

    const startX = node.position.x + node.width;
    const startY = node.position.y + node.height / 2;

    setDraggingFrom({ fieldId, startX, startY });
    setGhostEnd({ x: startX + 20, y: startY });
  }, [flow.nodes]);

  useEffect(() => {
    if (!draggingFrom) return;

    const handleMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left - flow.pan.x) / flow.zoom;
      const y = (e.clientY - rect.top - flow.pan.y) / flow.zoom;
      setGhostEnd({ x, y });
    };

    const handleUp = (e: MouseEvent) => {
      // Check if dropped on an input port
      const target = e.target as HTMLElement;
      const port = target.closest('[data-port="input"]');
      if (port && draggingFrom) {
        const targetFieldId = port.getAttribute('data-field-id');
        if (targetFieldId && targetFieldId !== draggingFrom.fieldId) {
          flow.addConnection(draggingFrom.fieldId, targetFieldId);
        }
      }

      setDraggingFrom(null);
      setGhostEnd(null);
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [draggingFrom, flow]);

  // Fit to view on mount (runs once when nodes first appear)
  const hasFittedRef = useRef(false);
  useEffect(() => {
    if (!hasFittedRef.current && containerRef.current && flow.nodes.length > 0) {
      hasFittedRef.current = true;
      const rect = containerRef.current.getBoundingClientRect();
      flow.fitToView(rect.width, rect.height);
    }
  }, [flow.nodes.length, flow.fitToView]);

  // Selected connection details for editor
  const selectedConnection = flow.connections.find(c => c.id === flow.selectedConnectionId);
  const selectedConnectionSourceField = selectedConnection
    ? fields.find(f => f.id === selectedConnection.sourceFieldId)
    : null;
  const selectedConnectionSourceNode = selectedConnection
    ? flow.nodes.find(n => n.fieldId === selectedConnection.sourceFieldId)
    : null;

  const conditionEditorPosition = selectedConnectionSourceNode
    ? {
        x: selectedConnectionSourceNode.position.x + selectedConnectionSourceNode.width + 16,
        y: selectedConnectionSourceNode.position.y,
      }
    : { x: 0, y: 0 };

  // Story 5.7: page-level validation overlay (orphan pages, page loops)
  const pageValidation = useMemo(() => {
    if (!pages || pages.length === 0) return null;
    const resolved = resolvePages({ fields, pages } as Form);
    return validateFlowIntegrity(fields, resolved);
  }, [fields, pages]);

  const pageNameById = useMemo(() => {
    const map = new Map<string, string>();
    (pages || []).forEach((p, i) => map.set(p.id, p.title?.trim() || `Página ${i + 1}`));
    return map;
  }, [pages]);

  const pageIssueMessages = useMemo(() => {
    if (!pageValidation) return [] as { type: 'orphan_page' | 'page_loop'; message: string }[];
    const out: { type: 'orphan_page' | 'page_loop'; message: string }[] = [];
    pageValidation.orphanPages.forEach((pid) => {
      out.push({ type: 'orphan_page', message: `Página "${pageNameById.get(pid) || pid}" é inalcançável` });
    });
    pageValidation.loopPagePaths.forEach((cycle) => {
      const labels = cycle.map((pid) => `"${pageNameById.get(pid) || pid}"`).join(' → ');
      out.push({ type: 'page_loop', message: `Loop entre páginas: ${labels}` });
    });
    return out;
  }, [pageValidation, pageNameById]);

  const issueCount = showValidation ? flow.validationIssues.length + pageIssueMessages.length : 0;

  // Compute implicit sequential connections (visual-only, not stored in state)
  const implicitConnections = useMemo(() => {
    const result: Array<{ id: string; sourceFieldId: string; targetFieldId: string; isDefault: boolean }> = [];
    fields.forEach((field, idx) => {
      if (idx >= fields.length - 1) return;
      const nextFieldId = fields[idx + 1].id;
      // Skip if there's already an explicit connection between these fields
      const hasExplicit = flow.connections.some(
        c => c.sourceFieldId === field.id && c.targetFieldId === nextFieldId
      );
      if (hasExplicit) return;
      result.push({
        id: `implicit_${field.id}_${nextFieldId}`,
        sourceFieldId: field.id,
        targetFieldId: nextFieldId,
        isDefault: true,
      });
    });
    return result;
  }, [fields, flow.connections]);

  // Empty state
  if (!fields.length) {
    return (
      <div className={`flex items-center justify-center h-full ${className}`}>
        <div className="text-center">
          <p className="text-neutral-500 text-sm">Nenhum campo no formulário.</p>
          <p className="text-neutral-600 text-xs mt-1">Adicione campos para visualizar o fluxo.</p>
        </div>
      </div>
    );
  }

  // Z-INDEX HIERARCHY (internal to FlowEditor):
  // z-0   Background grid (dot pattern via CSS)
  // z-10  SVG Layer: connections (pointerEvents: none)
  // z-20  HTML Layer: nodes (default)
  // z-30  HTML Layer: selected node (elevated)
  // z-40  FlowConditionEditor (popover above nodes)
  // ---
  // OVERLAYS (outside canvas transform, absolute in container):
  // z-50  Floating Toolbar (bottom-right)
  // z-50  Validation Panel (top-left)

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden bg-neutral-950 rounded-xl ${className}`}
      style={{
        backgroundImage: 'radial-gradient(circle, #333 1px, transparent 1px)',
        backgroundSize: '24px 24px',
        cursor: isPanning ? 'grabbing' : 'grab',
        touchAction: 'none',
      }}
      onMouseDown={handleCanvasMouseDown}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* SVG Layer: Connections */}
      <svg
        className="absolute inset-0 w-full h-full"
        style={{ pointerEvents: 'none' }}
      >
        <defs>
          <marker
            id="arrowhead"
            viewBox="0 0 10 7"
            refX="10"
            refY="3.5"
            markerWidth="8"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <polygon points="0 0, 10 3.5, 0 7" fill="#6366f1" />
          </marker>
          <marker
            id="arrowhead-dim"
            viewBox="0 0 10 7"
            refX="10"
            refY="3.5"
            markerWidth="6"
            markerHeight="5"
            orient="auto-start-reverse"
          >
            <polygon points="0 0, 10 3.5, 0 7" fill="#3f3f46" opacity="0.6" />
          </marker>
        </defs>

        <g transform={`translate(${flow.pan.x}, ${flow.pan.y}) scale(${flow.zoom})`}>
          {/* Implicit sequential connections (visual-only) */}
          <g>
            {implicitConnections.map(conn => {
              const sourceNode = flow.nodes.find(n => n.fieldId === conn.sourceFieldId);
              const targetNode = flow.nodes.find(n => n.fieldId === conn.targetFieldId);
              if (!sourceNode || !targetNode) return null;
              return (
                <FlowConnection
                  key={conn.id}
                  connection={conn as any}
                  sourceNode={sourceNode}
                  targetNode={targetNode}
                  isSelected={false}
                  isLoop={false}
                  isImplicit
                  onSelect={() => {}}
                />
              );
            })}
          </g>

          {/* Rendered connections */}
          <g style={{ pointerEvents: 'stroke' }}>
            {flow.connections.map(conn => {
              const sourceNode = flow.nodes.find(n => n.fieldId === conn.sourceFieldId);
              const targetNode = flow.nodes.find(n => n.fieldId === conn.targetFieldId);
              if (!sourceNode || !targetNode) return null;

              const isLoop = flow.validationIssues.some(
                i => i.type === 'loop' && i.fieldIds.includes(conn.sourceFieldId) && i.fieldIds.includes(conn.targetFieldId)
              );

              return (
                <FlowConnection
                  key={conn.id}
                  connection={conn}
                  sourceNode={sourceNode}
                  targetNode={targetNode}
                  isSelected={flow.selectedConnectionId === conn.id}
                  isLoop={isLoop}
                  onSelect={flow.selectConnection}
                />
              );
            })}
          </g>

          {/* Ghost connection during drag */}
          {draggingFrom && ghostEnd && (
            <GhostConnection
              startX={draggingFrom.startX}
              startY={draggingFrom.startY}
              endX={ghostEnd.x}
              endY={ghostEnd.y}
            />
          )}
        </g>
      </svg>

      {/* HTML Layer: Nodes */}
      <div
        className="absolute inset-0"
        style={{
          transform: `translate(${flow.pan.x}px, ${flow.pan.y}px) scale(${flow.zoom})`,
          transformOrigin: '0 0',
          pointerEvents: 'none',
        }}
      >
        <div style={{ pointerEvents: 'auto' }}>
          {flow.nodes.map(node => {
            const field = fields.find(f => f.id === node.fieldId);
            if (!field) return null;

            const outgoingCount = flow.connections.filter(
              c => c.sourceFieldId === node.fieldId && !c.isDefault
            ).length;

            return (
              <FlowNode
                key={node.fieldId}
                field={field}
                node={node}
                isSelected={flow.selectedNodeId === node.fieldId}
                validationIssues={showValidation ? flow.validationIssues : []}
                outgoingCount={outgoingCount}
                onSelect={flow.selectNode}
                onMove={flow.moveNode}
                onStartConnection={handleStartConnection}
                onOpenRules={handleOpenRules}
                zoom={flow.zoom}
              />
            );
          })}
        </div>

        {/* Condition editor popover */}
        {selectedConnection && selectedConnectionSourceField && (
          <FlowConditionEditor
            connection={selectedConnection}
            sourceField={selectedConnectionSourceField}
            position={conditionEditorPosition}
            zoom={flow.zoom}
            pan={flow.pan}
            onUpdateCondition={flow.updateConnectionCondition}
            onRemoveConnection={flow.removeConnection}
            onClose={() => flow.selectConnection(null)}
          />
        )}
      </div>

      {/* Floating Toolbar — z-50: above all canvas elements */}
      <div className="absolute bottom-4 right-4 z-50 flex items-center gap-1 p-1 liquid-glass rounded-xl">
        <button
          onClick={flow.zoomIn}
          className="p-2 rounded-lg text-neutral-400 hover:text-white hover:bg-neutral-700/50 transition-colors"
          title="Zoom in"
        >
          <ZoomIn className="w-4 h-4" />
        </button>
        <span className="text-xs text-neutral-500 w-10 text-center">
          {Math.round(flow.zoom * 100)}%
        </span>
        <button
          onClick={flow.zoomOut}
          className="p-2 rounded-lg text-neutral-400 hover:text-white hover:bg-neutral-700/50 transition-colors"
          title="Zoom out"
        >
          <ZoomOut className="w-4 h-4" />
        </button>
        <div className="w-px h-5 bg-neutral-700/50 mx-0.5" />
        <button
          onClick={() => {
            if (containerRef.current) {
              const rect = containerRef.current.getBoundingClientRect();
              flow.fitToView(rect.width, rect.height);
            }
          }}
          className="p-2 rounded-lg text-neutral-400 hover:text-white hover:bg-neutral-700/50 transition-colors"
          title="Ajustar à tela"
        >
          <Maximize2 className="w-4 h-4" />
        </button>
        <button
          onClick={flow.resetLayout}
          className="p-2 rounded-lg text-neutral-400 hover:text-white hover:bg-neutral-700/50 transition-colors"
          title="Resetar layout"
        >
          <RotateCcw className="w-4 h-4" />
        </button>
        <div className="w-px h-5 bg-neutral-700/50 mx-0.5" />
        <button
          onClick={() => setShowValidation(prev => !prev)}
          className={`p-2 rounded-lg transition-colors ${
            showValidation
              ? issueCount > 0
                ? 'text-amber-400 bg-amber-500/10'
                : 'text-green-400 bg-green-500/10'
              : 'text-neutral-400 hover:text-white hover:bg-neutral-700/50'
          }`}
          title={`Validação ${showValidation ? 'ativa' : 'inativa'} (${flow.validationIssues.length} issues)`}
        >
          <AlertTriangle className="w-4 h-4" />
          {showValidation && flow.validationIssues.length > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-amber-500 text-white text-[9px] flex items-center justify-center font-bold">
              {flow.validationIssues.length}
            </span>
          )}
        </button>
      </div>

      {/* Validation panel — z-50: above all canvas elements */}
      {showValidation && (flow.validationIssues.length > 0 || pageIssueMessages.length > 0) && (
        <div className="absolute top-4 left-4 z-50 w-64 liquid-glass rounded-xl p-3 space-y-1.5">
          <p className="text-xs font-medium text-neutral-200 mb-2">
            Problemas no fluxo ({flow.validationIssues.length + pageIssueMessages.length})
          </p>
          {flow.validationIssues.slice(0, 5).map((issue, i) => (
            <div
              key={`field-${i}`}
              className={`text-xs px-2 py-1.5 rounded-lg ${
                issue.type === 'loop'
                  ? 'bg-red-500/10 text-red-400'
                  : issue.type === 'dead_end'
                  ? 'bg-amber-500/10 text-amber-400'
                  : 'bg-neutral-800 text-neutral-400'
              }`}
            >
              {issue.message}
            </div>
          ))}
          {pageIssueMessages.slice(0, 5).map((issue, i) => (
            <div
              key={`page-${i}`}
              className={`text-xs px-2 py-1.5 rounded-lg ${
                issue.type === 'page_loop'
                  ? 'bg-amber-500/10 text-amber-400'
                  : 'bg-purple-500/10 text-purple-400'
              }`}
              title={issue.type === 'page_loop' ? 'Loop entre páginas (aviso — pode ser intencional)' : 'Página inalcançável a partir do início'}
            >
              {issue.message}
            </div>
          ))}
          {flow.validationIssues.length + pageIssueMessages.length > 10 && (
            <p className="text-xs text-neutral-500">
              +{flow.validationIssues.length + pageIssueMessages.length - 10} mais...
            </p>
          )}
        </div>
      )}

      {/* Rules Modal */}
      {rulesModalField && (
        <RuleEditorModal
          isOpen={!!rulesModalField}
          onClose={() => setRulesModalFieldId(null)}
          field={rulesModalField}
          allFields={fields}
          scoringVariables={scoringVariables}
          onUpdateRules={handleUpdateRules}
          pages={pages}
        />
      )}
    </div>
  );
};
